import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import type { EnrichedNotification } from '../types';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../services/notificationService';

export interface UseNotificationsResult {
  notifications: EnrichedNotification[];
  unreadCount: number;
  isLoading: boolean;
  isMarkingAllRead: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const POLLING_INTERVAL_MS = 60000; // 60 seconds (Free-tier safe)

export function useNotifications(userId?: string | null): UseNotificationsResult {
  const [notifications, setNotifications] = useState<EnrichedNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef<boolean>(true);

  // Refresh unread count
  const refreshUnreadCount = useCallback(async () => {
    if (!userId) {
      if (isMountedRef.current) setUnreadCount(0);
      return;
    }
    try {
      const count = await fetchUnreadCount(supabase, userId);
      if (isMountedRef.current) {
        setUnreadCount(count);
      }
    } catch {
      // Non-blocking fail silently for badge
    }
  }, [userId]);

  // Refresh notifications list
  const refreshNotifications = useCallback(async () => {
    if (!userId) {
      if (isMountedRef.current) {
        setNotifications([]);
        setUnreadCount(0);
      }
      return;
    }

    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const data = await fetchNotifications(supabase, userId, { limit: 30 });
      if (isMountedRef.current) {
        setNotifications(data);
        const unread = data.filter((n) => !n.is_read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [userId]);

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!userId || !notificationId) return;

      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await markNotificationAsRead(supabase, notificationId, userId);
      } catch {
        // Roll back on error if needed
        await refreshNotifications();
      }
    },
    [userId, refreshNotifications]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    setIsMarkingAllRead(true);

    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);

    try {
      await markAllNotificationsAsRead(supabase, userId);
    } catch {
      await refreshNotifications();
    } finally {
      if (isMountedRef.current) {
        setIsMarkingAllRead(false);
      }
    }
  }, [userId, refreshNotifications]);

  // Initial fetch and 60-second polling interval
  useEffect(() => {
    isMountedRef.current = true;

    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Initial load
    refreshUnreadCount();

    // 60-second background polling while session active
    const timer = setInterval(() => {
      refreshUnreadCount();
    }, POLLING_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      clearInterval(timer);
    };
  }, [userId, refreshUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isMarkingAllRead,
    error,
    refreshNotifications,
    refreshUnreadCount,
    markAsRead,
    markAllAsRead,
  };
}
